/**
 * Simulação determinística — o coração do projeto.
 *
 * ESTADO DO MARCO: M2. Sobre o M1 (terreno, colisão de margem, morte/respawn)
 * entram as entidades (navio, helicóptero, depósito), o tiro, o
 * reabastecimento, a pontuação e as vidas extras. Jato, pontes/seções e a
 * curva de dificuldade chegam no M3 dentro deste mesmo contrato: `step()`,
 * `capture()`, `restore()`, `checksum()` não mudam de forma.
 *
 * Ordem fixa de um tick (parte do contrato de determinismo):
 *   scroll → janela de entidades → entidades movem → aviões (input, fuel,
 *   colisões, refuel, tiro) → balas (movem, colidem) → tick++
 *
 * Regras de pureza em §5.3, vigiadas por lint: sem relógio, sem Math.random,
 * sem transcendental. Tempo em ticks inteiros; estado em ponto fixo Q8.
 */
import { Checksum } from '../checksum.js';
import { BRIDGE, BULLET, ENTITY, FUEL, GEN, LIVES, PLAYER, SCORE, SCROLL, SCREEN, WORLD, isBridgeChunk } from '../config.js';
import { approach, clamp, fp, fpToPx } from '../fixed.js';
import { generateChunk } from '../generator/river.js';
import { RngService } from '../rng.js';
const HALF_HITBOX = fp(PLAYER.HITBOX_W / 2);
const MIN_X = fp(PLAYER.HITBOX_W / 2);
const MAX_X = fp(SCREEN.WIDTH - PLAYER.HITBOX_W / 2);
const AHEAD_Q8 = fp(PLAYER.WORLD_Y_AHEAD_PX);
/**
 * Limites do voo vertical do co-op, em Q8, derivados das margens de TELA.
 *
 * `yOffset` positivo empurra o avião para CIMA (mais adiante no rio), porque
 * a tela mapeia worldY maior em y menor. Daí o limite de cima sair de
 * `BASE_Y` e o de baixo do fundo da área de jogo.
 */
const Y_FREE_MAX = fp(PLAYER.BASE_Y - PLAYER.Y_FREE_TOP_PX);
const Y_FREE_MIN = fp(PLAYER.BASE_Y - (SCREEN.PLAYFIELD_HEIGHT - PLAYER.Y_FREE_BOTTOM_PX));
/** Entidade acorda quando seu topo cruza screenY = −32 (§8). */
const WAKE_MARGIN_Q8 = fp(SCREEN.PLAYFIELD_HEIGHT - ENTITY.ACTIVATION_Y);
/** Folga que a entidade mantém da margem ao quicar. */
const BOUNCE_MARGIN_Q8 = fp(2);
function entityHalfW(type) {
    switch (type) {
        case 'ship':
            return fp(ENTITY.SHIP.W / 2);
        case 'heli':
            return fp(ENTITY.HELI.W / 2);
        case 'depot':
            return fp(ENTITY.DEPOT.W / 2);
        case 'jet':
            return fp(ENTITY.JET.W / 2);
        case 'bridge':
            // Barreira sobre todo o rio (§8): a sobreposição horizontal é decidida
            // à parte — este valor nunca é consultado para pontes.
            return fp(SCREEN.WIDTH / 2);
    }
}
function entityHalfH(type) {
    switch (type) {
        case 'ship':
            return fp(ENTITY.SHIP.H / 2);
        case 'heli':
            return fp(ENTITY.HELI.H / 2);
        case 'depot':
            return fp(ENTITY.DEPOT.H / 2);
        case 'jet':
            return fp(ENTITY.JET.H / 2);
        case 'bridge':
            return fp(BRIDGE.H / 2);
    }
}
function entityScore(type, mode) {
    switch (type) {
        case 'ship':
            return SCORE.SHIP;
        case 'heli':
            return SCORE.HELI;
        case 'depot':
            // Em co-op, destruir o recurso do próprio time vale pouco (§9).
            return mode === 'solo' ? SCORE.DEPOT_SOLO : SCORE.DEPOT_COOP;
        case 'jet':
            return SCORE.JET;
        case 'bridge':
            return SCORE.BRIDGE;
    }
}
export class Sim {
    mode;
    playerCount;
    world;
    worldSeed;
    rng;
    state;
    /**
     * Cache de chunks. NÃO é estado: cada entrada é função pura de
     * (worldSeed, index, flag) e é regenerável a qualquer momento — por isso
     * fica fora de capture/restore/checksum, e iterá-lo para poda é inofensivo.
     */
    chunkCache = new Map();
    constructor(options) {
        this.mode = options.mode;
        this.playerCount = options.playerCount;
        this.world = options.world;
        this.worldSeed = options.world.kind === 'procedural' ? options.world.seed : options.world.fillSeed;
        this.rng = new RngService(this.worldSeed);
        this.state = {
            tick: 0,
            scrollSpeed: SCROLL.CRUISE_FP,
            scrollDist: 0,
            rngState: this.rng.snapshot,
            // A partida começa como um respawn: chunk 0 é reta segura (§6.1).
            safeChunks: [0],
            spawnedChunks: [],
            sectionsCleared: 0,
            teamLives: LIVES.COOP_POOL_START[options.playerCount - 1] ?? LIVES.COOP_POOL_START[0],
            nextTeamExtraAt: SCORE.EXTRA_LIFE_EVERY,
            externalJets: 0,
            planes: [],
            entities: [],
            bullets: [],
        };
        // Spawn dentro do span da linha inicial, distribuído por slot — e não em
        // fatias da tela: com rio estreito, fatia de tela poderia nascer na terra.
        const spawnLine = (PLAYER.WORLD_Y_AHEAD_PX / SCREEN.TILE) | 0;
        const span = this.terrainSpanAtLine(spawnLine);
        for (let slot = 0; slot < options.playerCount; slot++) {
            const frac = (slot + 1) * fp(span.rightPx - span.leftPx);
            const x = fp(span.leftPx) + Math.round(frac / (options.playerCount + 1));
            this.state.planes.push({
                x,
                yOffset: 0,
                fuel: FUEL.TANK,
                state: 'alive',
                score: 0,
                nextExtraAt: SCORE.EXTRA_LIFE_EVERY,
                // Corrida (§12.3): 1 vida — morreu, acabou; vence a distância.
                lives: options.mode === 'race' ? 1 : LIVES.SOLO_START,
                stateTicks: 0,
                invulnTicks: 0,
                fireCooldown: 0,
                refueling: false,
            });
        }
        this.syncEntityWindow();
    }
    get tick() {
        return this.state.tick;
    }
    get scrollDist() {
        return this.state.scrollDist;
    }
    /** Distância percorrida em px inteiros — HUD e ranking. */
    get distancePx() {
        return fpToPx(this.state.scrollDist);
    }
    get scrollSpeed() {
        return this.state.scrollSpeed;
    }
    get planes() {
        return this.state.planes;
    }
    get entities() {
        return this.state.entities;
    }
    get bullets() {
        return this.state.bullets;
    }
    /** Pontes destruídas — a seção mostrada no HUD (§10). */
    get sectionsCleared() {
        return this.state.sectionsCleared;
    }
    /** Pool de vidas da equipe (co-op). No solo, ignorar. */
    get teamLives() {
        return this.state.teamLives;
    }
    /** Score da equipe: soma dos aviões (§11). No solo, o score do único avião. */
    get totalScore() {
        let total = 0;
        for (const plane of this.state.planes)
            total += plane.score;
        return total;
    }
    /** Fim de jogo: solo = sem tentativas; co-op = pool 0 e ninguém voando. */
    get gameOver() {
        if (this.mode === 'solo' || this.mode === 'race') {
            for (const plane of this.state.planes) {
                if (plane.state !== 'gone')
                    return false;
            }
            return true;
        }
        if (this.state.teamLives > 0)
            return false;
        for (const plane of this.state.planes) {
            if (plane.state === 'alive' || plane.state === 'exploding' || plane.state === 'respawning')
                return false;
        }
        return true;
    }
    /** Linha do mundo sob o avião do slot. Exposta para render e debug. */
    worldLineOf(slot) {
        const plane = this.state.planes[slot];
        if (plane === undefined)
            return 0;
        return this.lineOfPlane(plane);
    }
    /**
     * Span navegável da linha `line` do mundo — lookup O(1) no chunk (§7.4).
     * Usado pela colisão aqui dentro e pelo render do cliente lá fora: os dois
     * enxergam exatamente o mesmo terreno porque só existe este caminho.
     */
    terrainSpanAtLine(line) {
        const index = (line / WORLD.CHUNK_LINES) | 0;
        const chunk = this.chunkAt(index);
        const i = line - index * WORLD.CHUNK_LINES;
        return {
            leftPx: chunk.leftPx[i] ?? 0,
            rightPx: chunk.rightPx[i] ?? SCREEN.WIDTH,
            islandLeftPx: chunk.islandLeftPx[i] ?? 0,
            islandRightPx: chunk.islandRightPx[i] ?? 0,
            safe: chunk.safe,
        };
    }
    /**
     * O intervalo [xQ8−halfW, xQ8+halfW] cabe em ALGUM canal navegável da
     * linha? Com ilha, os canais são dois; sem, um (§7.4). É o único critério
     * de terreno do jogo — avião e bala passam pelos mesmos olhos.
     */
    fitsChannel(span, xQ8, halfW) {
        const lo = xQ8 - halfW;
        const hi = xQ8 + halfW;
        if (span.islandRightPx > span.islandLeftPx) {
            return ((lo >= fp(span.leftPx) && hi <= fp(span.islandLeftPx)) ||
                (lo >= fp(span.islandRightPx) && hi <= fp(span.rightPx)));
        }
        return lo >= fp(span.leftPx) && hi <= fp(span.rightPx);
    }
    /**
     * Avança um tick. `inputs[slot]` é o input daquele avião neste tick.
     *
     * É a única porta de entrada do estado — o que permite rebobinar e reaplicar
     * na reconciliação do guest (§13.6) e reexecutar uma run inteira na
     * revalidação de replay (§14.4).
     */
    step(inputs) {
        const alpha = this.mode === 'solo' ? SCROLL.ALPHA_SOLO : SCROLL.ALPHA_COOP;
        // Em CO-OP o cenário rola em velocidade constante e ↑/↓ movem o próprio
        // avião (§12.2). A regra anterior — velocidade do grupo pela média das
        // intenções — era injogável a partir de 2 pessoas: a média era truncada
        // para inteiro, então `1/2` e `2/3` viravam 0 e só a UNANIMIDADE mudava a
        // velocidade. Com 3 jogadores, ninguém conseguia acelerar nem frear.
        let targetSpeed = SCROLL.CRUISE_FP;
        if (!this.isCoop) {
            let thrustSum = 0;
            let aliveCount = 0;
            for (let slot = 0; slot < this.state.planes.length; slot++) {
                const plane = this.state.planes[slot];
                if (plane === undefined || plane.state !== 'alive')
                    continue;
                thrustSum += inputs[slot]?.thrust ?? 0;
                aliveCount++;
            }
            targetSpeed = this.targetScrollSpeed(thrustSum, aliveCount);
        }
        this.state.scrollSpeed = approach(this.state.scrollSpeed, targetSpeed, alpha.num, alpha.den);
        this.state.scrollDist += this.state.scrollSpeed;
        this.syncEntityWindow();
        this.stepEntities();
        for (let slot = 0; slot < this.state.planes.length; slot++) {
            const plane = this.state.planes[slot];
            if (plane === undefined)
                continue;
            this.stepPlane(plane, inputs[slot], slot);
        }
        this.stepBullets();
        this.state.tick++;
        this.state.rngState = this.rng.snapshot;
        this.pruneChunkCache();
    }
    /**
     * Interferência da corrida (§12.3): injeta 1 jato EXTERNO — chega como
     * evento de rede quando um oponente destrói uma ponte. Entra com ID
     * próprio (chunk −1, slot sequencial) e por isso NÃO quebra o determinismo
     * do mundo base: chunks continuam funções puras da seed; o jato extra é
     * estado desta partida, como qualquer input.
     */
    injectJet() {
        const plane = this.state.planes[0] ?? this.emptyPlane();
        // Linha à frente do avião: já dentro da janela de aviso, cruzará visível.
        const line = this.lineOfPlane(plane) + ((SCREEN.PLAYFIELD_HEIGHT / SCREEN.TILE) | 0);
        const dir = this.rng.chance(1, 2) ? 1 : -1;
        const speedPx = this.rng.intBetween(ENTITY.JET.SPEED_MIN, ENTITY.JET.SPEED_MAX);
        const startX = dir === 1 ? -ENTITY.JET.OFFSCREEN_PX : SCREEN.WIDTH + ENTITY.JET.OFFSCREEN_PX;
        this.state.entities.push({
            chunk: -1,
            slot: this.state.externalJets++,
            type: 'jet',
            line,
            x: fp(startX),
            // px/s → Q8/tick com a mesma aritmética exata do gerador.
            vx: Math.round((speedPx * 256) / 60),
            dir,
            alive: true,
            awake: true,
            waitTicks: ENTITY.JET.EXTERNAL_WARNING_TICKS,
            stock: -1,
        });
        this.state.rngState = this.rng.snapshot;
    }
    // ── Entidades ──────────────────────────────────────────────────────────────
    /**
     * Mantém instanciadas as entidades dos chunks da janela visível+margem.
     * Chunk que entra na janela ganha suas entidades a partir dos spawns puros
     * do gerador; chunk que sai leva as suas embora — inclusive as mortas, que
     * renascem se o chunk voltar (rebobinada de respawn = trecho se joga de
     * novo, entidades de volta, fiel ao original).
     */
    syncEntityWindow() {
        const chunkLo = Math.max(0, ((this.distancePx / SCREEN.TILE) | 0) / WORLD.CHUNK_LINES) | 0;
        const chunkHi = chunkLo + 2;
        let dirty = false;
        for (const spawned of this.state.spawnedChunks) {
            if (spawned < chunkLo || spawned > chunkHi) {
                dirty = true;
                break;
            }
        }
        if (dirty) {
            this.state.spawnedChunks = this.state.spawnedChunks.filter((c) => c >= chunkLo && c <= chunkHi);
            // chunk −1 = entidade EXTERNA (interferência): vive fora da janela de
            // chunks; morre por conta própria ao sair da tela.
            this.state.entities = this.state.entities.filter((e) => e.chunk === -1 || (e.chunk >= chunkLo && e.chunk <= chunkHi));
        }
        // Poda de externos mortos ou deixados para trás pelo scroll.
        if (this.state.externalJets > 0) {
            const currentLine = (this.distancePx / SCREEN.TILE) | 0;
            this.state.entities = this.state.entities.filter((e) => e.chunk !== -1 || (e.alive && e.line > currentLine - 8));
        }
        for (let index = chunkLo; index <= chunkHi; index++) {
            if (this.state.spawnedChunks.includes(index))
                continue;
            this.state.spawnedChunks.push(index);
            for (const spawn of this.chunkAt(index).spawns) {
                this.state.entities.push({
                    chunk: index,
                    slot: spawn.slot,
                    type: spawn.type,
                    line: spawn.line,
                    x: spawn.xQ8,
                    vx: spawn.vxQ8,
                    dir: spawn.dir,
                    alive: true,
                    awake: false,
                    waitTicks: 0,
                    stock: spawn.type === 'depot' && this.mode !== 'solo' ? FUEL.DEPOT_STOCK_COOP : -1,
                });
            }
        }
    }
    stepEntities() {
        for (const entity of this.state.entities) {
            if (!entity.alive)
                continue;
            // Acorda ao entrar na tela (§8): topo da entidade cruza screenY −32.
            if (!entity.awake) {
                const worldY = fp(entity.line * SCREEN.TILE + SCREEN.TILE / 2);
                if (worldY < this.state.scrollDist + WAKE_MARGIN_Q8) {
                    entity.awake = true;
                    // Jato anuncia a entrada: 300 ms de aviso antes de cruzar (§8).
                    if (entity.type === 'jet')
                        entity.waitTicks = ENTITY.JET.WARNING_TICKS;
                }
                else {
                    continue;
                }
            }
            if (entity.type === 'jet') {
                if (entity.waitTicks > 0) {
                    entity.waitTicks--;
                    continue;
                }
                // Cruza a tela por cima de tudo — não respeita o rio (§8).
                entity.x += entity.vx * entity.dir;
                const gone = entity.x < fp(-ENTITY.JET.OFFSCREEN_PX - ENTITY.JET.W) ||
                    entity.x > fp(SCREEN.WIDTH + ENTITY.JET.OFFSCREEN_PX + ENTITY.JET.W);
                if (gone)
                    entity.alive = false;
                continue;
            }
            if (entity.vx === 0)
                continue;
            // Patrulha/deriva horizontal quicando nas bordas do PRÓPRIO canal —
            // uma ilha divide o rio, e a entidade pertence a um lado dela (§8).
            const span = this.terrainSpanAtLine(entity.line);
            const half = entityHalfW(entity.type);
            let loPx = span.leftPx;
            let hiPx = span.rightPx;
            if (span.islandRightPx > span.islandLeftPx) {
                if (entity.x < fp(span.islandLeftPx))
                    hiPx = span.islandLeftPx;
                else
                    loPx = span.islandRightPx;
            }
            entity.x += entity.vx * entity.dir;
            const lo = fp(loPx) + half + BOUNCE_MARGIN_Q8;
            const hi = fp(hiPx) - half - BOUNCE_MARGIN_Q8;
            if (entity.x <= lo) {
                entity.x = lo;
                entity.dir = 1;
            }
            else if (entity.x >= hi) {
                entity.x = hi;
                entity.dir = -1;
            }
        }
    }
    // ── Aviões ─────────────────────────────────────────────────────────────────
    stepPlane(plane, input, slot) {
        switch (plane.state) {
            case 'alive': {
                const moveX = input?.moveX ?? 0;
                if (moveX !== 0) {
                    plane.x = clamp(plane.x + moveX * PLAYER.LATERAL_SPEED_FP, MIN_X, MAX_X);
                }
                // Voo vertical: só em co-op. No solo ↑/↓ continuam sendo acelerador e
                // freio do cenário, que é o toque clássico do modo — e lá não há
                // conflito de intenção para resolver.
                const thrust = input?.thrust ?? 0;
                if (this.isCoop && thrust !== 0) {
                    plane.yOffset = clamp(plane.yOffset + thrust * PLAYER.Y_SPEED_FP, Y_FREE_MIN, Y_FREE_MAX);
                }
                // Combustível por TEMPO, não por distância (§9).
                plane.fuel -= FUEL.CONSUMPTION_PER_TICK;
                // Reabastecimento (§9): sobrevoar depósito vivo enche 25 un/s.
                plane.refueling = false;
                const overDepot = this.depotUnder(plane);
                if (overDepot !== null && overDepot.stock !== 0) {
                    plane.fuel = Math.min(FUEL.TANK, plane.fuel + FUEL.CONSUMPTION_PER_TICK + FUEL.REFUEL_PER_TICK);
                    plane.refueling = true;
                    // Estoque compartilhado (§9): dois aviões juntos drenam em dobro.
                    // Reabastecer vira decisão de equipe.
                    if (overDepot.stock > 0) {
                        overDepot.stock = Math.max(0, overDepot.stock - FUEL.REFUEL_PER_TICK);
                    }
                }
                if (plane.fuel <= 0) {
                    plane.fuel = 0;
                    this.kill(plane);
                    return;
                }
                // Colisões letais: margem (§7.4) e entidade hostil (§8). Depósito NÃO
                // é letal — sobrevoá-lo é justamente como se reabastece.
                if (plane.invulnTicks === 0) {
                    if (this.hitsTerrain(plane) || this.hitsHostileEntity(plane)) {
                        this.kill(plane);
                        return;
                    }
                }
                // Tiro (§7.2): cooldown + máximo de 3 projéteis vivos por jogador.
                if (plane.fireCooldown > 0)
                    plane.fireCooldown--;
                if ((input?.fire ?? false) && plane.fireCooldown === 0 && this.aliveBulletsOf(slot) < BULLET.MAX_ALIVE_PER_PLAYER) {
                    this.spawnBullet(plane, slot);
                    plane.fireCooldown = BULLET.COOLDOWN_TICKS;
                }
                if (plane.invulnTicks > 0)
                    plane.invulnTicks--;
                return;
            }
            case 'exploding': {
                plane.stateTicks--;
                if (plane.stateTicks > 0)
                    return;
                if (this.mode === 'solo' || this.mode === 'race') {
                    if (plane.lives <= 0) {
                        plane.state = 'gone';
                        return;
                    }
                    this.soloRespawn(plane);
                    return;
                }
                // Co-op (§12.2): respawn consome 1 vida do POOL; pool zerado vira
                // espectador — segue o grupo e volta de graça na próxima ponte.
                if (this.state.teamLives <= 0) {
                    plane.state = 'spectating';
                    return;
                }
                this.state.teamLives--;
                plane.state = 'respawning';
                plane.stateTicks = PLAYER.RESPAWN_DELAY_TICKS_COOP;
                return;
            }
            case 'respawning': {
                plane.stateTicks--;
                if (plane.stateTicks > 0)
                    return;
                this.respawnInPlace(plane);
                return;
            }
            case 'spectating':
            case 'gone':
                return;
        }
    }
    /** Depósito vivo sob o avião, ou null. */
    depotUnder(plane) {
        const planeY = this.state.scrollDist + AHEAD_Q8 + plane.yOffset;
        for (const entity of this.state.entities) {
            if (entity.type !== 'depot' || !entity.alive || !entity.awake)
                continue;
            if (this.overlapsPlane(plane, planeY, entity))
                return entity;
        }
        return null;
    }
    hitsHostileEntity(plane) {
        const planeY = this.state.scrollDist + AHEAD_Q8 + plane.yOffset;
        for (const entity of this.state.entities) {
            if (entity.type === 'depot' || !entity.alive || !entity.awake)
                continue;
            if (this.overlapsPlane(plane, planeY, entity))
                return true;
        }
        return false;
    }
    overlapsPlane(plane, planeY, entity) {
        const entY = fp(entity.line * SCREEN.TILE + SCREEN.TILE / 2);
        const halfH = entityHalfH(entity.type) + fp(PLAYER.HITBOX_H / 2);
        if (planeY - entY > halfH || entY - planeY > halfH)
            return false;
        // Ponte é barreira sobre TODO o rio (§8): na linha dela, não há desvio.
        if (entity.type === 'bridge')
            return true;
        const halfW = entityHalfW(entity.type) + HALF_HITBOX;
        return plane.x - entity.x <= halfW && entity.x - plane.x <= halfW;
    }
    kill(plane) {
        plane.state = 'exploding';
        plane.stateTicks = PLAYER.EXPLOSION_TICKS;
        plane.lives--;
        plane.refueling = false;
    }
    /** Soma pontos e concede vidas extras a cada 10.000 (§11). */
    award(plane, points) {
        plane.score += points;
        if (this.mode === 'solo' || this.mode === 'race') {
            while (plane.score >= plane.nextExtraAt) {
                plane.nextExtraAt += SCORE.EXTRA_LIFE_EVERY;
                // Reserva máxima de 6 além da ativa (§11).
                plane.lives = Math.min(plane.lives + 1, LIVES.SOLO_MAX_RESERVE + 1);
            }
            return;
        }
        // Co-op: o limiar é do SCORE DO TIME e a vida entra no pool (máx. 9).
        while (this.totalScore >= this.state.nextTeamExtraAt) {
            this.state.nextTeamExtraAt += SCORE.EXTRA_LIFE_EVERY;
            this.state.teamLives = Math.min(this.state.teamLives + 1, LIVES.COOP_POOL_MAX);
        }
    }
    // ── Balas ──────────────────────────────────────────────────────────────────
    aliveBulletsOf(slot) {
        let count = 0;
        for (const bullet of this.state.bullets) {
            if (bullet.alive && bullet.ownerSlot === slot)
                count++;
        }
        return count;
    }
    spawnBullet(plane, slot) {
        // Nasce no nariz do avião.
        const worldY = this.state.scrollDist + AHEAD_Q8 + plane.yOffset + fp(PLAYER.HITBOX_H / 2);
        // Reusa um slot morto do pool antes de crescer o array (§8: pooling).
        for (const bullet of this.state.bullets) {
            if (!bullet.alive) {
                bullet.ownerSlot = slot;
                bullet.x = plane.x;
                bullet.worldY = worldY;
                bullet.alive = true;
                return;
            }
        }
        this.state.bullets.push({ ownerSlot: slot, x: plane.x, worldY, alive: true });
    }
    stepBullets() {
        for (const bullet of this.state.bullets) {
            if (!bullet.alive)
                continue;
            // 400 px/s RELATIVOS À TELA (§7.2): no mundo, soma o scroll do tick.
            bullet.worldY += BULLET.SPEED_FP + this.state.scrollSpeed;
            // Saiu pelo topo da tela.
            if (bullet.worldY > this.state.scrollDist + fp(SCREEN.PLAYFIELD_HEIGHT)) {
                bullet.alive = false;
                continue;
            }
            // Terreno (§7.2): projétil morre na margem OU na ilha, com fagulha
            // (fagulha é do cliente; aqui só morre).
            const line = (fpToPx(bullet.worldY) / SCREEN.TILE) | 0;
            const span = this.terrainSpanAtLine(line);
            const halfW = fp(BULLET.W / 2);
            if (!this.fitsChannel(span, bullet.x, halfW)) {
                bullet.alive = false;
                continue;
            }
            // Entidades: primeira viva e acordada que sobrepõe.
            for (const entity of this.state.entities) {
                if (!entity.alive || !entity.awake)
                    continue;
                // Jato em aviso ainda está fora da tela: intocável até entrar.
                if (entity.type === 'jet' && entity.waitTicks > 0)
                    continue;
                const entY = fp(entity.line * SCREEN.TILE + SCREEN.TILE / 2);
                const halfH = entityHalfH(entity.type) + fp(BULLET.H / 2);
                if (bullet.worldY - entY > halfH || entY - bullet.worldY > halfH)
                    continue;
                if (entity.type !== 'bridge') {
                    // Ponte: qualquer bala na linha acerta (barreira, §8).
                    const halfWSum = entityHalfW(entity.type) + halfW;
                    if (bullet.x - entity.x > halfWSum || entity.x - bullet.x > halfWSum)
                        continue;
                }
                entity.alive = false;
                bullet.alive = false;
                const shooter = this.state.planes[bullet.ownerSlot];
                if (shooter !== undefined)
                    this.award(shooter, entityScore(entity.type, this.mode));
                // Ponte destruída encerra a seção (§10) e REVIVE os caídos de graça
                // (§12.2): a ponte é objetivo de resgate além dos 500 pts.
                if (entity.type === 'bridge') {
                    this.state.sectionsCleared++;
                    if (this.mode !== 'solo') {
                        for (const fallen of this.state.planes) {
                            if (fallen.state === 'spectating')
                                this.reviveInPlace(fallen);
                        }
                    }
                }
                break;
            }
        }
    }
    // ── Morte e respawn ────────────────────────────────────────────────────────
    /**
     * Respawn solo (§7.3): ~1 tela atrás da morte, sobre uma RETA_SEGURA que o
     * gerador injeta regenerando o chunk com a flag. Tanque cheio, 2 s de
     * invulnerabilidade. A rebobinada desfaz distância — o trecho se joga de
     * novo, com as entidades de volta.
     */
    soloRespawn(plane) {
        const deathLine = this.lineOfPlane(plane);
        const targetLine = deathLine - GEN.RESPAWN_REWIND_LINES;
        let chunkIndex = Math.max(0, (targetLine / WORLD.CHUNK_LINES) | 0);
        // Flagar um chunk de ponte como reta segura apagaria a ponte — o marco
        // que precisa ser destruído para passar. Recua um chunk.
        if (isBridgeChunk(chunkIndex))
            chunkIndex = Math.max(0, chunkIndex - 1);
        this.flagSafeChunk(chunkIndex);
        const line = chunkIndex * WORLD.CHUNK_LINES + GEN.RESPAWN_SAFE_LINE;
        const span = this.terrainSpanAtLine(line);
        plane.x = this.channelCenter(span);
        plane.yOffset = 0;
        plane.fuel = FUEL.TANK;
        plane.state = 'alive';
        plane.stateTicks = 0;
        plane.invulnTicks = PLAYER.INVULN_TICKS;
        const worldYPx = line * SCREEN.TILE + SCREEN.TILE / 2;
        this.state.scrollDist = Math.max(0, fp(worldYPx - PLAYER.WORLD_Y_AHEAD_PX));
        this.state.scrollSpeed = SCROLL.CRUISE_FP;
        // O mundo rebobinou: balas em voo pertencem ao trecho desfeito.
        for (const bullet of this.state.bullets)
            bullet.alive = false;
        this.syncEntityWindow();
    }
    /** Revive de ponte (§12.2): volta ao ar sem custo, centrado e protegido. */
    reviveInPlace(plane) {
        this.respawnInPlace(plane);
    }
    /** Respawn co-op: volta no lugar, no canal mais largo, tanque cheio. */
    respawnInPlace(plane) {
        const span = this.terrainSpanAtLine(this.lineOfPlane(plane));
        plane.x = this.channelCenter(span);
        plane.yOffset = 0;
        plane.fuel = FUEL.TANK;
        plane.state = 'alive';
        plane.stateTicks = 0;
        plane.invulnTicks = PLAYER.INVULN_TICKS;
    }
    flagSafeChunk(chunkIndex) {
        if (!this.state.safeChunks.includes(chunkIndex)) {
            this.state.safeChunks.push(chunkIndex);
            while (this.state.safeChunks.length > 4)
                this.state.safeChunks.shift();
        }
        // A flag muda o conteúdo do chunk: cópias sem flag estão velhas.
        this.chunkCache.delete(chunkIndex);
        // Entidades instanciadas do chunk agora seguro morrem com ele (§6.2:
        // reta segura não tem entidades) — remove e deixa reinstanciar vazio.
        if (this.state.spawnedChunks.includes(chunkIndex)) {
            this.state.spawnedChunks = this.state.spawnedChunks.filter((c) => c !== chunkIndex);
            this.state.entities = this.state.entities.filter((e) => e.chunk !== chunkIndex);
        }
    }
    /** Centro do canal mais largo da linha — reta segura não tem ilha, mas o
     *  respawn multi pode cair em linha com ilha. */
    channelCenter(span) {
        if (span.islandRightPx > span.islandLeftPx) {
            const leftW = span.islandLeftPx - span.leftPx;
            const rightW = span.rightPx - span.islandRightPx;
            return leftW >= rightW
                ? Math.round((fp(span.leftPx) + fp(span.islandLeftPx)) / 2)
                : Math.round((fp(span.islandRightPx) + fp(span.rightPx)) / 2);
        }
        return Math.round((fp(span.leftPx) + fp(span.rightPx)) / 2);
    }
    hitsTerrain(plane) {
        const span = this.terrainSpanAtLine(this.lineOfPlane(plane));
        return !this.fitsChannel(span, plane.x, HALF_HITBOX);
    }
    lineOfPlane(plane) {
        const worldYQ8 = this.state.scrollDist + AHEAD_Q8 + plane.yOffset;
        return (fpToPx(worldYQ8) / SCREEN.TILE) | 0;
    }
    chunkAt(index) {
        const cached = this.chunkCache.get(index);
        if (cached !== undefined)
            return cached;
        const chunk = generateChunk(this.worldSeed, index, this.state.safeChunks.includes(index), this.playerCount);
        this.chunkCache.set(index, chunk);
        return chunk;
    }
    pruneChunkCache() {
        if (this.chunkCache.size <= 12)
            return;
        const currentIndex = (this.lineOfPlane(this.state.planes[0] ?? this.emptyPlane()) / WORLD.CHUNK_LINES) | 0;
        // Iterar o cache é seguro: ele não é estado (ver declaração acima).
        for (const key of this.chunkCache.keys()) {
            if (key < currentIndex - 2 || key > currentIndex + 3)
                this.chunkCache.delete(key);
        }
    }
    emptyPlane() {
        return {
            x: 0,
            yOffset: 0,
            fuel: 0,
            state: 'gone',
            score: 0,
            nextExtraAt: SCORE.EXTRA_LIFE_EVERY,
            lives: 0,
            stateTicks: 0,
            invulnTicks: 0,
            fireCooldown: 0,
            refueling: false,
        };
    }
    /** Co-op local e online compartilham as regras (§12.2). */
    get isCoop() {
        return this.mode === 'coopLocal' || this.mode === 'coopOnline';
    }
    /** Só o SOLO usa isto: em co-op o scroll é constante. */
    targetScrollSpeed(thrustSum, aliveCount) {
        if (aliveCount === 0)
            return SCROLL.CRUISE_FP;
        const mean = (thrustSum / aliveCount) | 0;
        if (mean > 0)
            return SCROLL.BOOST_FP;
        if (mean < 0)
            return SCROLL.BRAKE_FP;
        return SCROLL.CRUISE_FP;
    }
    // ── Snapshot, rewind e checksum ────────────────────────────────────────────
    /** Cópia profunda do estado — para o rewind da reconciliação (§13.6). */
    capture() {
        return {
            tick: this.state.tick,
            scrollSpeed: this.state.scrollSpeed,
            scrollDist: this.state.scrollDist,
            rngState: this.state.rngState,
            safeChunks: [...this.state.safeChunks],
            spawnedChunks: [...this.state.spawnedChunks],
            sectionsCleared: this.state.sectionsCleared,
            teamLives: this.state.teamLives,
            nextTeamExtraAt: this.state.nextTeamExtraAt,
            externalJets: this.state.externalJets,
            planes: this.state.planes.map((plane) => ({ ...plane })),
            entities: this.state.entities.map((entity) => ({ ...entity })),
            bullets: this.state.bullets.map((bullet) => ({ ...bullet })),
        };
    }
    /** Restaura um estado capturado. */
    restore(snapshot) {
        this.state = {
            tick: snapshot.tick,
            scrollSpeed: snapshot.scrollSpeed,
            scrollDist: snapshot.scrollDist,
            rngState: snapshot.rngState,
            safeChunks: [...snapshot.safeChunks],
            spawnedChunks: [...snapshot.spawnedChunks],
            sectionsCleared: snapshot.sectionsCleared,
            teamLives: snapshot.teamLives,
            nextTeamExtraAt: snapshot.nextTeamExtraAt,
            externalJets: snapshot.externalJets,
            planes: snapshot.planes.map((plane) => ({ ...plane })),
            entities: snapshot.entities.map((entity) => ({ ...entity })),
            bullets: snapshot.bullets.map((bullet) => ({ ...bullet })),
        };
        this.rng.restore(snapshot.rngState);
        // As flags de reta segura podem ter mudado entre capture e restore — o
        // cache regenera sob as flags restauradas.
        this.chunkCache.clear();
    }
    /**
     * Checksum do estado corrente.
     *
     * A ordem de absorção é parte do contrato: mudar a ordem invalida os valores
     * golden dos testes — que é exatamente o alarme desejado, porque significa
     * que replays antigos deixaram de ser comparáveis (bump de RULES_VERSION).
     */
    checksum() {
        const sum = new Checksum();
        sum.push(this.state.tick);
        sum.push(this.state.scrollSpeed);
        sum.push(this.state.scrollDist);
        sum.push(this.state.rngState);
        sum.push(this.state.safeChunks.length);
        for (const index of this.state.safeChunks)
            sum.push(index);
        sum.push(this.state.spawnedChunks.length);
        for (const index of this.state.spawnedChunks)
            sum.push(index);
        sum.push(this.state.sectionsCleared);
        sum.push(this.state.teamLives);
        sum.push(this.state.nextTeamExtraAt);
        sum.push(this.state.externalJets);
        for (const plane of this.state.planes) {
            sum.push(plane.x);
            sum.push(plane.yOffset);
            sum.push(plane.fuel);
            sum.push(plane.score);
            sum.push(plane.nextExtraAt);
            sum.push(plane.lives);
            sum.push(plane.stateTicks);
            sum.push(plane.invulnTicks);
            sum.push(plane.fireCooldown);
            sum.pushBool(plane.refueling);
            sum.pushText(plane.state);
        }
        for (const entity of this.state.entities) {
            sum.push(entity.chunk);
            sum.push(entity.slot);
            sum.push(entity.line);
            sum.push(entity.x);
            sum.push(entity.vx);
            sum.push(entity.dir);
            sum.push(entity.waitTicks);
            sum.push(entity.stock);
            sum.pushBool(entity.alive);
            sum.pushBool(entity.awake);
            sum.pushText(entity.type);
        }
        for (const bullet of this.state.bullets) {
            sum.push(bullet.ownerSlot);
            sum.push(bullet.x);
            sum.push(bullet.worldY);
            sum.pushBool(bullet.alive);
        }
        return sum.value;
    }
}
//# sourceMappingURL=sim.js.map